import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useEmpresasAdmin } from '../../../hooks/useEmpresasAdmin';
import toast from 'react-hot-toast';
import { toISODateSafe } from '../../../utils/dateUtils';
import EmpresasTable from '../../../components/admin/empresas/EmpresasTable';
import EmpresaForm from '../../../components/admin/empresas/EmpresaForm';
import UsuariosEmpresaModal from '../../../components/admin/empresas/UsuariosEmpresaModal';
import { EmpresasAdminService } from '../../../services';
import type { 
  EmpresaListItem, 
  EmpresasFiltros, 
  PaginatedResponse 
} from '../../../types';

const EmpresasAdminView: React.FC = () => {
  const { t } = useTranslation();
  const [filtros, setFiltros] = useState<EmpresasFiltros>({
    page: 1,
    per_page: 20,
    orden: 'nombre',
    direccion: 'asc'
  });
  
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [empresaEditando, setEmpresaEditando] = useState<number | undefined>();
  const [empresaGestionUsuarios, setEmpresaGestionUsuarios] = useState<EmpresaListItem | null>(null);

  const {
    data: empresasData,
    isLoading: loadingEmpresas,
    error: errorEmpresas,
    refetch: refetchEmpresas
  } = useEmpresasAdmin(filtros);

  const handleFiltrosChange = (nuevosFiltros: EmpresasFiltros) => {
    setFiltros(nuevosFiltros);
  };

  const handleNuevaEmpresa = () => {
    setEmpresaEditando(undefined);
    setMostrarFormulario(true);
  };

  const handleEditarEmpresa = (empresa: EmpresaListItem) => {
    setEmpresaEditando(empresa.id);
    setMostrarFormulario(true);
  };

  const handleVerEmpresa = (empresa: EmpresaListItem) => {
    toast.success(`Viendo detalles de ${empresa.nombre}`);
  };

  const handleGestionarUsuarios = (empresa: EmpresaListItem) => {
    setEmpresaGestionUsuarios(empresa);
  };

  const handleExportar = async () => {
    try {
      toast.loading(`${t('common.loading')}`, { id: 'export' });
      
      const blob = await EmpresasAdminService.exportarEmpresas(filtros);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `empresas_${toISODateSafe(new Date())}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success(t('common.success'), { id: 'export' });
    } catch (error: any) {
      console.error('❌ Error al exportar empresas:', error);
      toast.error(t('errors.unknown_error'), { id: 'export' });
    }
  };

  const handleFormularioSuccess = () => {
    toast.success(t('common.success'));
    setMostrarFormulario(false);
    setEmpresaEditando(undefined);
    refetchEmpresas();
  };

  const handleFormularioClose = () => {
    setMostrarFormulario(false);
    setEmpresaEditando(undefined);
  };

  useEffect(() => {
    document.title = 'Administracion de Empresas - MAVA';
    return () => {
      document.title = 'MAVA';
    };
  }, []);

  useEffect(() => {
    if (errorEmpresas) {
      console.error('Error en query de empresas:', errorEmpresas);
      
      // Manejo específico para errores de permisos
      if ((errorEmpresas as any)?.response?.status === 403) {
        toast.error(t('errors.unknown_error'));
      } else if ((errorEmpresas as any)?.response?.status === 401) {
        toast.error(t('auth.session_expired'));
      } else {
        toast.error(t('errors.load_error'));
      }
    }
  }, [errorEmpresas]);

  // Mostrar mensaje de error de permisos si corresponde
  if (errorEmpresas && (errorEmpresas as any)?.response?.status === 403) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-xl">⚠️</span>
              </div>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">{t('auth.access_denied')}</h3>
              <p className="text-sm text-gray-500">{t('auth.access_denied_msg')}</p>
            </div>
          </div>
          <div className="text-sm text-gray-600 mb-4">
            <p>Se requieren permisos de administrador para acceder a esta sección.</p>
            <p className="mt-2">{t('auth.contact_admin')}</p>
          </div>
          <button
            onClick={() => window.history.back()}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            {t('common.back')}
          </button>
        </div>
      </div>
    );
  }

  const defaultData: PaginatedResponse<EmpresaListItem> = {
    items: [],
    total: 0,
    page: 1,
    per_page: 20,
    pages: 0,
    has_next: false,
    has_prev: false
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="md:flex md:items-center md:justify-between">
              <div className="flex-1 min-w-0">
                <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
                  {t('admin.title')}
                </h1>
                <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <span>Gestion integral de empresas y usuarios del sistema MAVA</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
                      <span className="text-green-600 font-semibold text-sm">E</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total {t('admin.companies')}
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">
                          {empresasData?.total || 0}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
                      <span className="text-blue-600 font-semibold text-sm">A</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        {t('admin.companies')} {t('common.active').toLowerCase()}s
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">
                          {empresasData?.items.filter(e => e.es_activa).length || 0}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-100 rounded-md flex items-center justify-center">
                      <span className="text-purple-600 font-semibold text-sm">U</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Usuarios
                      </dt>

                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">
                          {empresasData?.items.reduce((acc, emp) => acc + emp.usuarios_count, 0) || 0}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-100 rounded-md flex items-center justify-center">
                      <span className="text-yellow-600 font-semibold text-sm">R</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total {t('reports.title')}
                      </dt>
                      <dd className="flex items-baseline">
                        <div className="text-2xl font-semibold text-gray-900">
                          {empresasData?.items.reduce((acc, emp) => acc + emp.recursos_count.reportes, 0) || 0}
                        </div>
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <EmpresasTable
          data={empresasData || defaultData}
          filtros={filtros}
          loading={loadingEmpresas}
          onFiltrosChange={handleFiltrosChange}
          onVerEmpresa={handleVerEmpresa}
          onEditarEmpresa={handleEditarEmpresa}
          onGestionarUsuarios={handleGestionarUsuarios}
          onNuevaEmpresa={handleNuevaEmpresa}
          onExportar={handleExportar}
        />
      </div>

      <EmpresaForm
        isOpen={mostrarFormulario}
        onClose={handleFormularioClose}
        onSuccess={handleFormularioSuccess}
        empresaId={empresaEditando}
      />

      {/* Modal de gestión de usuarios */}
      {empresaGestionUsuarios && (
        <UsuariosEmpresaModal
          empresa={empresaGestionUsuarios}
          isOpen={!!empresaGestionUsuarios}
          onClose={() => setEmpresaGestionUsuarios(null)}
        />
      )}
    </div>
  );
};

export default EmpresasAdminView;
